import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { OperatorFunction, pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { CommitmentDtoTransformer } from '../coaching-commitment/commitment/commitment-dto.transformer';
import { ObjectDateTransformer } from '../coaching-insight/coaching-insight.model';
import { AddNotesData } from '../coaching/detail/../transcript-dialog/model/add-notes-data.model';
import {
    catchErrorLogRet,
    mapDelete,
    mapRetrieve,
    mapUpdate,
} from '../common/rest.operators';

import { CommitmentDiff } from './diff-decorator';
import {
    ApprovalPostDto,
    CommitmentApprovalDto,
    CommitmentDraftDto,
    CommitmentObservationLinkDto,
    CommitmentObservationLinkPostDto,
    CommitmentPostDto,
    NewCommitmentParamsPostDto,
} from './general';

@Injectable()
export class DraftCommitmentService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    newDraft<T>(
        project: () => {
            params: NewCommitmentParamsPostDto;
        },
    ): OperatorFunction<T, CommitmentDraftDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .post(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/new`,
                        args.params,
                    )
                    .pipe(
                        mapRetrieve(),
                        tap((e: CommitmentDraftDto) => {
                            e.commitment =
                                new CommitmentDtoTransformer().transform(
                                    e.commitment,
                                );
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    create<T>(
        project: () => {
            commitment: CommitmentPostDto;
        },
    ): OperatorFunction<T, CommitmentDraftDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .post(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments`,
                        args.commitment,
                    )
                    .pipe(
                        // to be really restful 201 + location header
                        mapUpdate(),
                        tap((e: CommitmentDraftDto) => {
                            e.commitment =
                                new CommitmentDtoTransformer().transform(
                                    e.commitment,
                                );
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    retrieve<T>(
        project: () => {
            draftId: string;
        },
    ): OperatorFunction<T, CommitmentDraftDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .get(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${args.draftId}`,
                    )
                    .pipe(
                        mapRetrieve(),
                        tap((e: CommitmentDraftDto) => {
                            e.commitment =
                                new CommitmentDtoTransformer().transform(
                                    e.commitment,
                                );
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    update<T>(
        project: () => {
            draftId: string;
            commitment: CommitmentPostDto;
        },
    ): OperatorFunction<T, CommitmentDraftDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .put(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${args.draftId}`,
                        args.commitment,
                    )
                    .pipe(
                        mapUpdate(),
                        tap((e: CommitmentDraftDto) => {
                            e.commitment =
                                new CommitmentDtoTransformer().transform(
                                    e.commitment,
                                );
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    delete<T>(project: () => string): OperatorFunction<T, boolean> {
        return pipe(
            switchMap(() => {
                const draftId = project();
                return this._http.delete(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${draftId}`,
                );
            }),
            mapDelete(),
        );
    }

    linkObservations<T>(
        project: () => {
            dto: CommitmentObservationLinkPostDto;
        },
    ): OperatorFunction<T, CommitmentObservationLinkDto[]> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .post(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/linkobservations`,
                        args.dto,
                    )
                    .pipe(mapUpdate());
            }),
            catchErrorLogRet(null),
        );
    }

    diff<T>(
        project: () => { draftId: string },
    ): OperatorFunction<T, CommitmentDiff> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .get(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${args.draftId}/diff`,
                    )
                    .pipe(
                        mapRetrieve(
                            new ObjectDateTransformer({
                                context: {
                                    estimatedCompletionDateBefore: 'L',
                                    sessionDate: null,
                                },
                            }),
                        ),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    send<T>(
        project: () => {
            draftId: string;
            approvalPostDto: ApprovalPostDto;
        },
    ): OperatorFunction<T, CommitmentApprovalDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .post(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${args.draftId}/send`,
                        args.approvalPostDto,
                    )
                    .pipe(
                        // to be really restful 201 + location header
                        mapUpdate(),
                        tap((e: CommitmentApprovalDto) => {
                            if (e.commitment) {
                                e.commitment =
                                    new CommitmentDtoTransformer().transform(
                                        e.commitment,
                                    );
                            }
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    retrieveApproval<T>(
        project: () => {
            approvalId: number;
        },
    ): OperatorFunction<T, CommitmentApprovalDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .get(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${args.approvalId}`,
                    )
                    .pipe(
                        mapRetrieve(),
                        tap((e: CommitmentDraftDto) => {
                            if (e.commitment) {
                                e.commitment =
                                    new CommitmentDtoTransformer().transform(
                                        e.commitment,
                                    );
                            }
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    signOff<T>(
        project: () => {
            approvalId: number;
            approvalPostDto: ApprovalPostDto;
        },
    ): OperatorFunction<T, CommitmentApprovalDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .put(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/${args.approvalId}/signoff`,
                        args.approvalPostDto,
                    )
                    .pipe(
                        mapUpdate(),
                        tap((e: CommitmentApprovalDto) => {
                            if (e.commitment) {
                                e.commitment =
                                    new CommitmentDtoTransformer().transform(
                                        e.commitment,
                                    );
                            }
                        }),
                    );
            }),
            catchErrorLogRet(null),
        );
    }

    addNotes<T>(
        project: () => {
            data: AddNotesData;
        },
    ): OperatorFunction<T, CommitmentObservationLinkDto[]> {
        return pipe(
            switchMap(() => {
                const args = project();
                return this._http
                    .post(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/draft-commitments/addnotes`,
                        args.data,
                    )
                    .pipe(mapUpdate());
            }),
            catchErrorLogRet(null),
        );
    }
}
